import type { ModifierGroup, ModifierOption } from '@/types'

/**
 * Effective cardinality for a group, once the current selections are taken
 * into account (F-MOD-N7 / owner decision D5).
 *
 * Picking "Large" can make the Flavours group demand 3 scoops where "Medium"
 * demands 2. Those rules are tenant data (modifier_option_group_rules), not
 * code, so a parlour changes its scoop counts without a deploy.
 *
 * Pure and separately tested because it drives both what the operator is told
 * and whether the confirm button lets them through — and because the server
 * enforces the same rule independently. If the two disagree, the counter
 * either blocks a valid order or lets through one the server will reject at
 * the worst possible moment.
 */

export interface EffectiveBounds {
  min: number
  /** null = unlimited. */
  max: number | null
}

export type SelectionMap = Record<string, Record<string, number>>

/** Total UNITS chosen in a group (sum of quantities, not distinct options). */
export function unitsInGroup(sel: SelectionMap, groupId: string): number {
  const g = sel[groupId]
  if (!g) return 0
  return Object.values(g).reduce((n, q) => n + (q > 0 ? q : 0), 0)
}

/**
 * Resolve each group's bounds after applying the rules carried by whatever is
 * currently selected.
 *
 * Later rules win over earlier ones — with at most one size selectable, and a
 * size being the only realistic rule source, a conflict means the menu is
 * misconfigured; picking a deterministic winner beats throwing at the counter.
 */
export function effectiveBounds(
  groups: ModifierGroup[],
  optionById: ReadonlyMap<string, ModifierOption>,
  sel: SelectionMap,
): Map<string, EffectiveBounds> {
  const out = new Map<string, EffectiveBounds>()
  for (const g of groups) out.set(g.id, { min: g.min_select, max: g.max_select })

  // Applied in option-id order to match the server's `ORDER BY option_id`.
  // Object key order here is insertion order — the sequence the cashier
  // happened to tap in — which would let the two sides resolve a
  // same-target collision differently.
  const chosen: string[] = []
  for (const groupId of Object.keys(sel)) {
    for (const [optionId, qty] of Object.entries(sel[groupId] ?? {})) {
      if (qty >= 1) chosen.push(optionId)
    }
  }
  chosen.sort()
  {
    for (const optionId of chosen) {
      const opt = optionById.get(optionId)
      for (const rule of opt?.group_rules ?? []) {
        const current = out.get(rule.target_group_id)
        if (!current) continue // rule points at a group this product lacks — inert
        out.set(rule.target_group_id, {
          min: rule.min_select ?? current.min,
          max: rule.max_select === undefined ? current.max : rule.max_select,
        })
      }
    }
  }
  return out
}

export interface GroupValidation {
  groupId: string
  ok: boolean
  units: number
  bounds: EffectiveBounds
  /** Operator-facing reason, empty when ok. */
  message: string
}

/** Human phrasing of a group's requirement, for the header of its section. */
export function requirementLabel(b: EffectiveBounds): string {
  if (b.min === 0 && b.max == null) return 'Optional'
  if (b.min === 0) return `Optional · up to ${b.max}`
  if (b.max == null) return `Pick at least ${b.min}`
  if (b.min === b.max) return b.min === 1 ? 'Pick 1' : `Pick ${b.min}`
  return `Pick ${b.min} to ${b.max}`
}

/**
 * Validate every group against its effective bounds.
 *
 * Mirrors resolveLineModifiers on the server, which applies the D5 rules
 * before checking cardinality for exactly the same reason.
 */
export function validateSelections(
  groups: ModifierGroup[],
  optionById: ReadonlyMap<string, ModifierOption>,
  sel: SelectionMap,
): GroupValidation[] {
  const bounds = effectiveBounds(groups, optionById, sel)
  return groups.map((g) => {
    const b = bounds.get(g.id) ?? { min: g.min_select, max: g.max_select }
    const units = unitsInGroup(sel, g.id)
    let message = ''
    if (units < b.min) {
      message =
        units === 0 && b.min === 1
          ? // A prompt that already reads as an instruction ("Choose a side" on a
            // deal, F-DEAL-N1) is used as is; otherwise "Choose <group name>".
            g.prompt && /^choose\b/i.test(g.prompt.trim())
            ? g.prompt.trim()
            : `Choose ${g.name.toLowerCase()}`
          : `Choose ${b.min - units} more`
    } else if (b.max != null && units > b.max) {
      message = `Remove ${units - b.max}`
    }
    return { groupId: g.id, ok: message === '', units, bounds: b, message }
  })
}

/** Flatten a selection map into the wire shape, dropping empties. */
export function selectionsToWire(
  sel: SelectionMap,
): Array<{ group_id: string; option_id: string; quantity: number }> {
  const out: Array<{ group_id: string; option_id: string; quantity: number }> = []
  for (const groupId of Object.keys(sel)) {
    for (const [optionId, quantity] of Object.entries(sel[groupId] ?? {})) {
      if (quantity > 0) out.push({ group_id: groupId, option_id: optionId, quantity })
    }
  }
  return out
}

/** Rebuild a selection map from stored wire selections. */
export function wireToSelections(
  wire: ReadonlyArray<{ group_id: string; option_id: string; quantity: number }>,
): SelectionMap {
  const out: SelectionMap = {}
  for (const s of wire) {
    if (s.quantity < 1) continue
    out[s.group_id] = { ...(out[s.group_id] ?? {}), [s.option_id]: s.quantity }
  }
  return out
}

/**
 * Pre-select every group's default options, respecting its bounds.
 *
 * A sold-out option is never pre-selected — the fast "add with defaults" path
 * must not silently commit the cashier to something the kitchen cannot make.
 */
export function defaultSelections(groups: ModifierGroup[]): SelectionMap {
  const out: SelectionMap = {}
  for (const g of groups) {
    const defaults = (g.options ?? []).filter((o) => o.is_default && o.is_available)
    if (defaults.length === 0) continue
    const cap = g.max_select ?? defaults.length
    const chosen = defaults.slice(0, Math.max(0, cap))
    if (chosen.length === 0) continue
    out[g.id] = Object.fromEntries(chosen.map((o) => [o.id, 1]))
  }
  return out
}

/**
 * The order the configurator lays a group's options out in.
 *
 * A size group (price_mode 'replace') reads cheapest to dearest, whatever
 * order the options were created in: an owner who added Large first should
 * not get "Large, Small, Medium" on the till. Every other group keeps the
 * menu's own order — add-ons are arranged by the owner on purpose.
 */
export function orderedOptions(g: ModifierGroup): ModifierOption[] {
  const opts = g.options ?? []
  if (g.price_mode !== 'replace') return opts
  return opts
    .map((o, i) => ({ o, i }))
    .sort((a, b) => a.o.price_delta - b.o.price_delta || a.i - b.i)
    .map(({ o }) => o)
}

/** One pick, no follow-up: the group can only ever hold a single option. */
export function isSingleChoice(g: ModifierGroup): boolean {
  return g.selection_type === 'single' || g.max_select === 1
}

/**
 * True when the whole sheet is one required single-choice question — a sized
 * item with nothing else to ask. The counter then adds the line on the pick
 * itself instead of making the cashier tap the option and then Add.
 *
 * Per-option repeats rule it out (there is a second thing to adjust), and so
 * does a second group, even an optional one: the cashier may want it.
 */
export function isOneTapGroup(groups: ModifierGroup[]): boolean {
  if (groups.length !== 1) return false
  const g = groups[0]
  return isSingleChoice(g) && g.min_select >= 1 && !g.allow_quantity
}

/**
 * The chosen options as the ticket will read them, in sheet order:
 * "Medium · Extra cheese ×2". Empty when nothing is chosen.
 */
export function selectionSummary(groups: ModifierGroup[], sel: SelectionMap): string {
  const parts: string[] = []
  for (const g of groups) {
    const chosen = sel[g.id] ?? {}
    for (const o of orderedOptions(g)) {
      const n = chosen[o.id] ?? 0
      if (n > 0) parts.push(n > 1 ? `${o.name} ×${n}` : o.name)
    }
  }
  return parts.join(' · ')
}
